import { DateTime } from "luxon";
import type { RosterActivityEvent } from "@/features/roster-activity";
import { COHORT_LABELS, KIND_LABELS } from "@/features/roster-activity";

export type RosterCopyFormat = "text" | "csv" | "tsv";

export type RosterCopyOptions = {
  format: RosterCopyFormat;
  includeDates: boolean;
};

const TABLE_HEADERS = ["Name", "PGA ID", "Change", "Detail", "Member type"] as const;

const EMPTY_PERIOD_COPY = "No roster activity in this period.";

function activityHeadline(event: RosterActivityEvent): string {
  return event.headline.replace(`${event.personName} `, "");
}

function formatOccurredAt(occurredAt: string): string {
  return DateTime.fromISO(occurredAt).toFormat("MMM d, yyyy");
}

function escapeDelimited(value: string, delimiter: "," | "\t"): string {
  const needsQuotes = delimiter === "," ? /[",\n\r]/.test(value) : /["\t\n\r]/.test(value);
  if (!needsQuotes) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/** Freeform detail when present; otherwise the person-stripped headline for context. */
function delimitedDetail(event: RosterActivityEvent): string {
  const detail = event.detail?.trim();
  if (detail) return detail;
  return activityHeadline(event);
}

function buildTextCopy(events: RosterActivityEvent[], includeDates: boolean): string {
  if (!events.length) return EMPTY_PERIOD_COPY;

  return events
    .map((event) => {
      const headline = activityHeadline(event);
      const detail = event.detail ? ` (${event.detail})` : "";
      const body = `${event.personName} — ${headline}${detail}`;
      if (!includeDates) return body;
      return `${formatOccurredAt(event.occurredAt)} — ${body}`;
    })
    .join("\n");
}

function buildDelimitedCopy(events: RosterActivityEvent[], includeDates: boolean, delimiter: "," | "\t"): string {
  if (!events.length) return EMPTY_PERIOD_COPY;

  const headers = [...(includeDates ? ["Date"] : []), ...TABLE_HEADERS];

  const rows = events.map((event) => {
    const cells = [
      ...(includeDates ? [formatOccurredAt(event.occurredAt)] : []),
      event.personName,
      event.pgaId ?? "",
      KIND_LABELS[event.kind],
      delimitedDetail(event),
      COHORT_LABELS[event.cohort]
    ];
    return cells.map((cell) => escapeDelimited(cell, delimiter)).join(delimiter);
  });

  return [headers.join(delimiter), ...rows].join("\n");
}

export function buildRosterCopy(events: RosterActivityEvent[], options: RosterCopyOptions): string {
  if (options.format === "csv") {
    return buildDelimitedCopy(events, options.includeDates, ",");
  }
  if (options.format === "tsv") {
    return buildDelimitedCopy(events, options.includeDates, "\t");
  }
  return buildTextCopy(events, options.includeDates);
}

/** Preview of the clipboard payload for the dialog (first few rows). */
export function buildRosterCopyPreview(events: RosterActivityEvent[], options: RosterCopyOptions, maxRows = 3): string {
  if (!events.length) {
    return buildRosterCopy([], options);
  }
  return buildRosterCopy(events.slice(0, maxRows), options);
}

export function rosterCopySuccessMessage(format: RosterCopyFormat): string {
  if (format === "csv") return "CSV copied";
  if (format === "tsv") return "Copied for Sheets / Excel";
  return "List copied";
}

/** @deprecated Prefer buildRosterCopy({ format: "text", includeDates: false }) */
export function buildPlainListCopy(events: RosterActivityEvent[]): string {
  return buildRosterCopy(events, { format: "text", includeDates: false });
}
